<template>
    <div 
        class="h-full flex flex-col transition-all duration-500 relative"
        :class="[
            isCollapsed 
                ? 'w-20 shadow-lg bg-white/95 dark:bg-surface-800/95 fixed left-0 top-0 bottom-0 ' 
                : 'w-64 bg-white/90 dark:bg-surface-800/90'
        ]"
    >
        <!-- Toggle collapse button centrato -->
        <button 
            class="hidden  shadow-lg lg:flex absolute -right-5 top-1/2 -translate-y-1/2 items-center justify-center w-10 h-10 bg-white dark:bg-surface-900 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group z-50"
            @click="toggleCollapse"
        >
            <span 
                class="material-symbols-outlined text-sm text-surface-400 group-hover:text-momap transition-all duration-300"
                :class="{ '-rotate-180': !isCollapsed }"
            >
                chevron_right
            </span>
        </button>

        <!-- Header -->
        <div class="flex items-center justify-center h-16">
            <img 
                :src="`//cdn.momap.it/branding/logos/${isCollapsed ? 'logo_red_icon.png' : darkMode ? 'logo.svg' : 'logo_red.svg'}`" 
                alt="MoMap" 
                :class="[
                    'transition-all duration-500 hover:scale-105',
                    isCollapsed ? 'w-8' : 'w-28'
                ]"
                lazy
            />
        </div>

        <!-- Menu items -->
        <div class="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-surface-200 dark:scrollbar-thumb-surface-700">
            <ul class="space-y-1 p-1">
                <template v-for="(item, index) in items" :key="index">
                    <li>
                        <component
                            :is="item.items ? 'button' : 'AppLink'"
                            v-ripple
                            :to="!item.items ? item.name : undefined"
                            class="menu-item w-full relative overflow-hidden"
                            :class="[
                                'flex items-center transition-all duration-300',
                                isCollapsed ? 'justify-center px-3 py-2' : 'px-4 py-3',
                                isActiveRoute(item) ? 'active' : ''
                            ]"
                            @click="item.items ? toggleSection(item) : handleNavigation(item)"
                            v-tooltip.right="isCollapsed ? item.label : ''"
                        >
                            <!-- Background animato (solo quando non è collassato) -->
                            <div 
                                v-if="!isCollapsed"
                                class="absolute inset-0 opacity-0 transition-all duration-300"
                                :class="[
                                    `bg-gradient-to-r ${item.gradient}`,
                                    isActiveRoute(item) ? '!opacity-100' : ''
                                ]"
                            ></div>

                            <div class="flex items-center gap-4" :class="{ 'flex-col': isCollapsed }">
                                <!-- Icona con background colorato quando attivo -->
                                <div 
                                    class="relative w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300"
                                    :class="[
                                        isActiveRoute(item) 
                                            ? isCollapsed
                                                ? `bg-gradient-to-r ${item.gradient}`
                                                : 'bg-white/20'
                                            : 'bg-surface-50 dark:bg-surface-900'
                                    ]"
                                >
                                    <span 
                                        class="material-symbols-outlined relative z-10 transition-all duration-300" 
                                        :class="isActiveRoute(item) ? 'text-white' : item.iconColor"
                                    >
                                        {{ item.icon }}
                                    </span>
                                </div>

                                <!-- Label -->
                                <span 
                                    v-if="!isCollapsed"
                                    class="font-medium relative z-10 transition-all duration-300"
                                    :class="isActiveRoute(item) ? 'text-white' : 'text-surface-700 dark:text-surface-200'"
                                >
                                    {{ item.label }}
                                </span>
                            </div>
                        </component>
                    </li>
                </template>
            </ul>
        </div>

        <!-- Footer -->
        <div class="shrink-0">
            <!-- Separatore con animazione -->
            <div class="relative h-px overflow-hidden">
                <div 
                    class="absolute inset-0 bg-gradient-to-r from-transparent via-surface-200 dark:via-surface-700 to-transparent animate-shimmer"
                ></div>
            </div>
            
            <!-- Footer items -->
            <div class="p-2 space-y-1">
                <!-- User info con tooltip -->
                <div class="flex items-center justify-center">
                    <button 
                        class="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-surface-50 dark:hover:bg-surface-700/50 transition-all duration-300 group"
                        v-tooltip.right="isCollapsed ? `${user?.name || 'Utente'} (${user?.email})` : ''"
                    >
                        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-violet-500/10 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                            <span class="material-symbols-outlined text-violet-500">person</span>
                        </div>
                        <div 
                            v-if="!isCollapsed" 
                            class="min-w-0 flex-1"
                        >
                            <div class="font-medium truncate">{{ user?.name || 'Utente' }}</div>
                            <div class="text-sm text-surface-500 truncate">{{ user?.email }}</div>
                        </div>
                    </button>
                </div>

                <!-- Theme toggle -->
                <div class="flex items-center justify-between p-2 h-[44px]">
                    <span 
                        v-if="!isCollapsed"
                        class="text-sm font-medium text-surface-600 dark:text-surface-400"
                    >
                        Tema
                    </span>
                    <ToggleTheme />
                </div>

                <!-- Logout button -->
                <div class="flex items-center justify-center">
                    <button 
                        @click="handleLogout"
                        class="w-full flex items-center gap-3 p-2 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all duration-300 group"
                        v-tooltip.right="isCollapsed ? 'Logout' : ''"
                    >
                        <div class="w-10 h-10 rounded-xl flex items-center justify-center">
                            <span class="material-symbols-outlined transition-transform duration-300 group-hover:scale-110">logout</span>
                        </div>
                        <span 
                            v-if="!isCollapsed" 
                            class="font-medium flex-1 text-left"
                        >
                            Logout
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDark } from '@vueuse/core';
import { handleErrorsSilent } from '@/helpers/errorHandler';
import navigationsList from "./utils/navigationsList";
import ToggleTheme from '@/components/reusable/ToggleTheme.vue';
import { useAuthStore } from '@/stores/auth';

const route = useRoute();
const router = useRouter();
const darkMode = useDark();
const authStore = useAuthStore();

const isCollapsed = ref(false);

// Osserva i cambiamenti della route per comprimere automaticamente la sidebar in alcune pagine
watch(() => route.name, (newRouteName) => {
    // Comprimiamo la sidebar quando navighiamo alla pagina dello storico email
    if (newRouteName === 'emailHistory' || newRouteName === 'template-generator') {
        isCollapsed.value = true;
    }
}, { immediate: true }); // immediate: true per applicare subito quando il componente è montato

// Colori unici per ogni menu
const menuColors = {
    'home': {
        color: 'orange-500',
        gradient: 'from-orange-500 via-orange-400 to-orange-300',
        iconColor: 'text-orange-500 dark:text-orange-400',
        label: 'Dashboard'
    },
    'emailHistory': {
        color: 'violet-500',
        gradient: 'from-violet-500 via-violet-400 to-violet-300',
        iconColor: 'text-violet-500 dark:text-violet-400'
    },
    'sendEmail': {
        color: 'cyan-500',
        gradient: 'from-cyan-500 via-cyan-400 to-cyan-300',
        iconColor: 'text-cyan-500 dark:text-cyan-400'
    },
    'contacts': {
        color: 'fuchsia-500',
        gradient: 'from-fuchsia-500 via-fuchsia-400 to-fuchsia-300',
        iconColor: 'text-fuchsia-500 dark:text-fuchsia-400'
    },
    'users': {
        color: 'rose-500',
        gradient: 'from-rose-500 via-rose-400 to-rose-300',
        iconColor: 'text-rose-500 dark:text-rose-400'
    },
    'templates': {
        color: 'green-500',
        gradient: 'from-green-500 via-green-400 to-green-300',
        iconColor: 'text-green-500 dark:text-green-400'
    },
    'settings': {
        color: 'amber-500',
        gradient: 'from-amber-500 via-amber-400 to-amber-300',
        iconColor: 'text-amber-500 dark:text-amber-400'
    }
};

// Modifichiamo la lista di navigazione
const items = ref(navigationsList.map(item => ({
    ...item,
    label: menuColors[item.name]?.label || item.label,
    color: menuColors[item.name]?.color || 'momap',
    gradient: menuColors[item.name]?.gradient || 'from-momap via-momap/90 to-momap/80',
    iconColor: menuColors[item.name]?.iconColor || 'text-momap dark:text-momap'
})));

const user = computed(() => authStore.getUser);

// Sezione corrente
const getCurrentSectionLabel = computed(() => {
    const activeItem = items.value.find(item => isActiveRoute(item));
    return activeItem ? activeItem.label : '';
});

// Verifica route attiva
const isActiveRoute = (item) => {
    if (item.items) {
        return item.items.some(subItem => subItem.name === route.name);
    }
    return item.name === route.name;
};

// Toggle sezione
const toggleSection = handleErrorsSilent(async (item) => {
    if (item?.items) {
        item.expanded = !item.expanded;
    }
});

const handleLogout = async () => {
    await authStore.logout();
    router.push('/login');
};

const emit = defineEmits(['navigate']);

// Modifichiamo handleNavigation per una transizione più fluida
const handleNavigation = (item) => {
    const menuItems = document.querySelectorAll('.menu-item');
    const currentActive = document.querySelector('.menu-item.active');
    const clickedItem = document.querySelector(`[href*="${item.name}"]`);
    
    if (currentActive === clickedItem) return;

    // Rimuoviamo la classe active dal precedente
    if (currentActive) {
        currentActive.classList.remove('active');
        currentActive.classList.add('leaving');
        
        // Aggiungiamo l'effetto particelle in uscita
        const particles = currentActive.querySelectorAll('.particle');
        particles.forEach(p => p.classList.add('explode'));
    }

    // Aggiungiamo la classe active al nuovo elemento
    if (clickedItem) {
        // Piccolo delay per l'animazione in entrata
        setTimeout(() => {
            menuItems.forEach(item => {
                item.classList.remove('leaving');
                item.querySelectorAll('.particle').forEach(p => p.classList.remove('explode'));
            });
            clickedItem.classList.add('active');
        }, 300);
    }

    emit('navigate');
};

const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
};
</script>

<style scoped>
/* Animazione shine */
@keyframes shine {
    0% {
        background: linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
        background-position: 200% 0;
    }
    100% {
        background: linear-gradient(45deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
        background-position: -200% 0;
    }
}

.animate-shine {
    animation: shine 1.5s infinite linear;
}

/* Animazione particelle migliorata */
.particle {
    width: var(--size);
    height: var(--size);
    opacity: 0;
    transform: translate(var(--x), var(--y)) scale(0);
    animation: float 3s ease-in-out infinite;
    animation-delay: var(--delay);
}

@keyframes float {
    0%, 100% {
        transform: translate(var(--x), var(--y)) scale(0);
        opacity: 0;
    }
    50% {
        transform: translate(calc(var(--x) + 30px), calc(var(--y) - 30px)) scale(1);
        opacity: 0.8;
    }
}

/* Scrollbar personalizzata */
.scrollbar-thin {
    scrollbar-width: thin;
}

.scrollbar-thumb-surface-200::-webkit-scrollbar-thumb {
    background-color: var(--surface-200);
    border-radius: 9999px;
}

/* Transizioni pagina */
.page-enter-active,
.page-leave-active {
    transition: all 0.5s ease-in-out;
}

.page-enter-from {
    opacity: 0;
    transform: translateX(-30px);
}

.page-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

/* Stili per il footer */
.footer-enter-active,
.footer-leave-active {
    transition: all 0.3s ease-in-out;
}

.footer-enter-from,
.footer-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

/* Animazione migliorata per l'elemento attivo */
.menu-item {
    position: relative;
    overflow: hidden;
}

.menu-item.active {
    @apply transform-none;
}

.menu-item:not(.active):hover {
    background: linear-gradient(to right, var(--surface-50), transparent);
}

.menu-item:not(.active):hover .material-symbols-outlined {
    transform: scale(1.1);
}

/* Animazione per il cambio di selezione */
.menu-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 0.25rem;
    background: currentColor;
    opacity: 0;
    transform: translateX(-100%);
    transition: all 0.3s ease;
}

.menu-item.active::before {
    opacity: 0.5;
    transform: translateX(0);
}

/* Effetto glow */
.menu-item.active::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at var(--x) var(--y), currentColor, transparent 70%);
    opacity: 0.1;
    transition: opacity 0.3s ease;
}

.menu-item.active:hover::after {
    opacity: 0.15;
}

/* Animazione per il collapse */
.menu-item {
    white-space: nowrap;
}

/* Miglioriamo l'effetto hover sul toggle button */
.group:hover .material-symbols-outlined {
    @apply text-momap;
}

/* Migliora il contrasto del pulsante toggle */
button[class*="hover:text-momap"]:hover {
    @apply bg-surface-100 dark:bg-surface-700;
}

/* Nascondi il pulsante toggle su mobile */
@media (max-width: 1024px) {
    .toggle-collapse {
        display: none;
    }
}

/* Animazione shimmer per il separatore */
@keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}

.animate-shimmer {
    animation: shimmer 2s infinite;
}

/* Hover effect sulla sidebar collassata */
.sidebar-collapsed:hover {
    @apply w-24;
    transition: width 0.3s ease;
}

/* Animazione per gli elementi del footer */
.footer-item {
    @apply transition-all duration-300;
}

.footer-item:hover {
    @apply transform scale-105;
}

/* Migliora l'animazione del toggle button */
.toggle-button {
    @apply transition-all duration-300;
}

.toggle-button:hover {
    @apply transform scale-110 rotate-[360deg];
}

/* Rimuovi il background colorato quando è compresso */
.menu-item.active.compressed {
    background: none;
}
</style> 